import React, { useEffect, useRef } from 'react';
import { Box } from '@ublend-npm/aulaui-core';

interface InfiniteScrollTriggerProps {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  rootMargin?: string;
  /** Scroll container to observe against. Defaults to the viewport. */
  rootRef?: React.RefObject<Element | null>;
}

const InfiniteScrollTrigger: React.FC<InfiniteScrollTriggerProps> = ({
  onLoadMore,
  hasMore,
  isLoadingMore,
  rootMargin = '100px',
  rootRef,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const triggerNode = triggerRef.current;
    if (!triggerNode || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore) {
          onLoadMore();
        }
      },
      {
        root: rootRef?.current ?? null,
        rootMargin,
        threshold: 0,
      }
    );

    observer.observe(triggerNode);

    return () => {
      observer.disconnect();
    };
  }, [onLoadMore, hasMore, isLoadingMore, rootMargin, rootRef]);

  if (!hasMore) return null;

  return <Box ref={triggerRef} width="100%" height="1px" visibility="hidden" />;
};

export default InfiniteScrollTrigger;
